import React, { useState, useEffect, useRef, useCallback } from 'react';
import useKioskManifest from '../hooks/useKioskManifest';

const API = process.env.REACT_APP_URL;
const KIOSK_AUTH_KEY = 'kiosk_auth';

/* ── Geo helpers ─────────────────────────────────────────────────────────── */
function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('GPS not supported'));
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      err => reject(err.code === 1
        ? new Error('Location access denied. Tap the lock icon in your address bar, set Location to "Allow", then reload.')
        : new Error('Unable to get location. Enable GPS and try again.')),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

function getSavedAuth() {
  try {
    const raw = localStorage.getItem(KIOSK_AUTH_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw);
    if (a.expiresAt && Date.now() < a.expiresAt) return a;
    localStorage.removeItem(KIOSK_AUTH_KEY);
    return null;
  } catch { return null; }
}

function todayISO() {
  return new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const ISSUE_OPTIONS = ['ID card missing', 'Wrong uniform', 'Incomplete uniform', 'Untidy appearance', 'Other'];

/* ── Secure image loader (sends auth headers via fetch, renders blob URL) ── */
function SecureImg({ src, alt, style, token, coords: c }) {
  const [blobUrl, setBlobUrl] = React.useState(null);
  React.useEffect(() => {
    if (!src || !token) return;
    var revoke;
    var hdrs = { 'Authorization': 'Bearer ' + token };
    if (c) { hdrs['x-userlat'] = String(c.lat); hdrs['x-userlon'] = String(c.lon); }
    fetch(src, { headers: hdrs })
      .then(function(r) { return r.ok ? r.blob() : null; })
      .then(function(blob) {
        if (blob) { var u = URL.createObjectURL(blob); setBlobUrl(u); revoke = u; }
      })
      .catch(function() {});
    return function() { if (revoke) URL.revokeObjectURL(revoke); };
  }, [src, token, c]);
  if (!blobUrl) return <div style={{ ...style, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#94a3b8' }}>Loading...</div>;
  return <img src={blobUrl} alt={alt} style={style} />;
}

/* ── Main component ─────────────────────────────────────────────────────── */
export default function KioskAttendance() {
  useKioskManifest('/manifest-attendance.json', '#1a237e');

  // Auth state
  const [kioskAuth, setKioskAuth] = useState(() => getSavedAuth());
  const [authUser, setAuthUser] = useState('');
  const [authOtp, setAuthOtp] = useState('');
  const [authStep, setAuthStep] = useState('username');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authResend, setAuthResend] = useState(0);
  const [authPassword, setAuthPassword] = useState('');
  const authTimerRef = useRef(null);
  const otpRef = useRef(null);
  const pwRef = useRef(null);

  // Geo
  const [geoState, setGeoState] = useState('checking');
  const [geoError, setGeoError] = useState('');
  const [coords, setCoords] = useState(null);

  // Tabs
  const [activeTab, setActiveTab] = useState('review');

  // Review Attendance
  const [reviewDate, setReviewDate] = useState(todayISO);
  const [selfies, setSelfies] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [otherText, setOtherText] = useState('');
  const [toast, setToast] = useState(null);

  // Leaves
  const [leaves, setLeaves] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [leaveComment, setLeaveComment] = useState({});

  // Install
  const [showInstall, setShowInstall] = useState(false);
  const deferredPrompt = useRef(null);

  // ── Helpers ──
  function authHeaders() {
    const h = { 'Content-Type': 'application/json' };
    if (kioskAuth?.token) {
      h['Authorization'] = 'Bearer ' + kioskAuth.token;
      if (kioskAuth.username) h['x-username'] = kioskAuth.username.toLowerCase();
    }
    if (coords) { h['x-userlat'] = String(coords.lat); h['x-userlon'] = String(coords.lon); }
    return h;
  }

  function showToast(msg, type) {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  // ── Dynamic favicon ──
  useEffect(() => {
    const c = document.createElement('canvas');
    c.width = 64; c.height = 64;
    const ctx = c.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 64, 64);
    g.addColorStop(0, '#1a237e'); g.addColorStop(1, '#3949ab');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(32, 32, 32, 0, Math.PI * 2); ctx.fill();
    // Checkmark icon
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(18, 34); ctx.lineTo(28, 44); ctx.lineTo(46, 22); ctx.stroke();
    ctx.font = 'bold 10px Arial'; ctx.fillStyle = '#fff'; ctx.textAlign = 'center';
    ctx.fillText('ATT', 32, 58);
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.rel = 'icon'; link.href = c.toDataURL();
    document.head.appendChild(link);
    document.title = 'MYC — Attendance Review';
  }, []);

  // ── Geo check ──
  useEffect(() => {
    (async () => {
      try {
        const c = await getGeo();
        setCoords(c);
        setGeoState('ok');
      } catch (err) {
        setGeoState(err.message.includes('denied') ? 'denied' : 'error');
        setGeoError(err.message);
      }
    })();
  }, []);

  // ── Auth handlers ──
  function startAuthResend() {
    setAuthResend(60);
    clearInterval(authTimerRef.current);
    authTimerRef.current = setInterval(() => {
      setAuthResend(t => { if (t <= 1) { clearInterval(authTimerRef.current); return 0; } return t - 1; });
    }, 1000);
  }

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!authUser.trim()) return setAuthError('Please enter your username.');
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(API + '/kiosk-otp/request', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: authUser.trim(), userLat: coords?.lat, userLon: coords?.lon }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');
      if (data.mode === 'password') { setAuthStep('password'); }
      else { setAuthStep('otp'); startAuthResend(); if (data.devOtp) setAuthError('[DEV] OTP: ' + data.devOtp); }
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const isPw = authStep === 'password';
    if (!isPw && (!authOtp || authOtp.length !== 4)) return setAuthError('Enter the 4-digit OTP.');
    if (isPw && !authPassword) return setAuthError('Enter your password.');
    setAuthLoading(true); setAuthError('');
    try {
      const body = { username: authUser.trim() };
      if (isPw) body.password = authPassword; else body.otp = authOtp.trim();
      const res = await fetch(API + '/kiosk-otp/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || (isPw ? 'Invalid password' : 'Invalid OTP'));
      const kioskDays = data.data.kioskSessionDays || 5;
      const authObj = { token: data.data.token, username: data.data.username, roles: data.data.roles || [], expiresAt: Date.now() + kioskDays * 24 * 60 * 60 * 1000 };
      localStorage.setItem(KIOSK_AUTH_KEY, JSON.stringify(authObj));
      setKioskAuth(authObj);
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  useEffect(() => {
    if (authStep === 'otp' && otpRef.current) otpRef.current.focus();
    if (authStep === 'password' && pwRef.current) pwRef.current.focus();
  }, [authStep]);

  // Install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); deferredPrompt.current = e; };
    window.addEventListener('beforeinstallprompt', handler);
    if (!localStorage.getItem('kiosk_att_prompted')) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS || !('beforeinstallprompt' in window)) {
        setShowInstall(true);
        localStorage.setItem('kiosk_att_prompted', '1');
      }
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
    }
    setShowInstall(false);
    localStorage.setItem('kiosk_att_prompted', '1');
  };

  // ── Data fetching ──
  const fetchSelfies = useCallback(async (date) => {
    if (!kioskAuth?.token || !date) return;
    setReviewLoading(true);
    try {
      const res = await fetch(API + '/myattendance/selfies/' + date, { headers: authHeaders() });
      const data = await res.json();
      setSelfies(res.ok ? (data || []) : []);
    } catch { setSelfies([]); }
    finally { setReviewLoading(false); }
  }, [kioskAuth, coords]); // eslint-disable-line

  const fetchLeaves = useCallback(async () => {
    if (!kioskAuth?.token) return;
    setLeavesLoading(true);
    try {
      const res = await fetch(API + '/appointments/type/Leave/', { headers: authHeaders() });
      const data = await res.json();
      setLeaves(res.ok ? (data || []) : []);
    } catch { setLeaves([]); }
    finally { setLeavesLoading(false); }
  }, [kioskAuth, coords]); // eslint-disable-line

  useEffect(() => {
    if (kioskAuth && activeTab === 'review') fetchSelfies(reviewDate);
  }, [kioskAuth, reviewDate, activeTab, fetchSelfies]);

  useEffect(() => {
    if (kioskAuth && activeTab === 'leaves') fetchLeaves();
  }, [kioskAuth, activeTab, fetchLeaves]);

  // ── Review actions ──
  const handleApprove = async (id) => {
    try {
      const res = await fetch(API + '/myattendance/uniform-review/' + id, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ status: 'approved', issues: [] }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      showToast('Approved!', 'success');
      fetchSelfies(reviewDate);
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleFlagIssue = (id) => {
    setReviewingId(id);
    setSelectedIssues([]);
    setOtherText('');
  };

  const submitIssue = async () => {
    const issues = [...selectedIssues];
    if (issues.includes('Other') && otherText.trim()) {
      issues[issues.indexOf('Other')] = otherText.trim();
    } else if (issues.includes('Other')) {
      issues.splice(issues.indexOf('Other'), 1);
    }
    if (issues.length === 0) return showToast('Select at least one issue', 'error');
    try {
      const res = await fetch(API + '/myattendance/uniform-review/' + reviewingId, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ status: 'issue', issues }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      setReviewingId(null);
      showToast('Issue flagged', 'success');
      fetchSelfies(reviewDate);
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleDeleteSelfie = async (id, username) => {
    if (!window.confirm('Delete selfie for ' + username + '? They will need to re-upload.')) return;
    try {
      const res = await fetch(API + '/myattendance/selfie/' + id, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      showToast('Deleted — re-upload required', 'success');
      fetchSelfies(reviewDate);
    } catch (err) { showToast(err.message, 'error'); }
  };

  // ── Leave actions ──
  const handleLeaveAction = async (id, action) => {
    try {
      const body = { approved: action };
      if (leaveComment[id]) body.comments = leaveComment[id];
      const res = await fetch(API + '/appointments/update/' + id, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed');
      showToast(action === 'approved' ? 'Leave approved' : 'Leave rejected', 'success');
      fetchLeaves();
    } catch (err) { showToast(err.message, 'error'); }
  };

  // ── Styles ──
  const pageStyle = { minHeight: '100vh', background: 'linear-gradient(135deg, #e8eaf6 0%, #ede7f6 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 12px 40px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
  const cardStyle = { background: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,.08)', width: '100%', maxWidth: 600, overflow: 'hidden' };
  const headerStyle = { background: 'linear-gradient(135deg, #1a237e, #3949ab)', padding: '20px 20px 16px', color: '#fff', textAlign: 'center' };
  const authInputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 16, background: '#fafafa', boxSizing: 'border-box', color: '#1e293b', textAlign: 'center', letterSpacing: 1 };

  // ── Geo screens ──
  if (geoState === 'checking') {
    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📍</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Checking location...</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Please allow location access when prompted.</div>
        </div>
      </div>
    );
  }

  if (geoState === 'denied' || geoState === 'error') {
    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={{ ...cardStyle, maxWidth: 400, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>Location Required</h2>
          <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{geoError || 'Enable GPS and try again.'}</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: 20, padding: '10px 24px', borderRadius: 8, border: 'none', background: '#1a237e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
        </div>
      </div>
    );
  }

  // ── Auth gate ──
  if (geoState === 'ok' && !kioskAuth) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, maxWidth: 420 }}>
          <div style={headerStyle}>
            <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 4, marginBottom: 4 }}>MYC</div>
            <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase' }}>Staff Authentication</div>
          </div>
          <div style={{ padding: '24px 28px 32px' }}>
            {authStep === 'username' && (
              <form onSubmit={handleRequestOtp}>
                <div style={{ fontSize: 14, color: '#475569', marginBottom: 16, textAlign: 'center', lineHeight: 1.5 }}>
                  Enter your staff username to continue.
                </div>
                <input type="text" value={authUser} onChange={e => { setAuthUser(e.target.value); setAuthError(''); }}
                  placeholder="Username" style={authInputStyle} autoComplete="off" autoFocus />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#1a237e' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : 'linear-gradient(135deg, #1a237e, #3949ab)',
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Checking...' : 'Continue'}
                </button>
              </form>
            )}
            {authStep === 'otp' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthOtp(''); setAuthError(''); }}
                    style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>
                  OTP sent to your registered WhatsApp number.
                </div>
                <input ref={otpRef} type="number" value={authOtp} onChange={e => { setAuthOtp(e.target.value.slice(0, 4)); setAuthError(''); }}
                  placeholder="4-digit OTP" style={authInputStyle} inputMode="numeric" />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#1a237e' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 10, textAlign: 'center' }}>
                  {authResend > 0 ? 'Resend in ' + authResend + 's' : (
                    <button type="button" onClick={handleRequestOtp} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a237e', fontSize: 12, textDecoration: 'underline' }}>Resend OTP</button>
                  )}
                </div>
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : 'linear-gradient(135deg, #1a237e, #3949ab)',
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </form>
            )}
            {authStep === 'password' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthPassword(''); setAuthError(''); }}
                    style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>
                  Enter your password to continue.
                </div>
                <input ref={pwRef} type="password" value={authPassword} onChange={e => { setAuthPassword(e.target.value); setAuthError(''); }}
                  placeholder="Password" style={{ ...authInputStyle, letterSpacing: 3 }} />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : 'linear-gradient(135deg, #1a237e, #3949ab)',
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Verifying...' : 'Login & Continue'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Sorted leaves ──
  var pendingLeaves = leaves.filter(l => l.approved === 'applied').sort((a, b) => new Date(b.start) - new Date(a.start));
  var otherLeaves = leaves.filter(l => l.approved !== 'applied').sort((a, b) => new Date(b.start) - new Date(a.start)).slice(0, 20);

  // ── Main content ──
  return (
    <div style={pageStyle}>
      {/* Install prompt */}
      {showInstall && (
        <div style={{ width: '100%', maxWidth: 600, background: '#ede7f6', border: '1px solid #b39ddb', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: 'linear-gradient(135deg, #1a237e, #3949ab)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(26,35,126,0.3)',
          }}>
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>ATT</span>
          </div>
          <div style={{ flex: 1, fontSize: 13, color: '#311b92', lineHeight: 1.5 }}>
            <strong>Quick Access:</strong> Tap <strong>Share → Add to Home Screen</strong> to install.
          </div>
          <button onClick={handleInstall} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a237e', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {deferredPrompt.current ? 'Install' : 'Got it'}
          </button>
        </div>
      )}

      <div style={{ ...cardStyle, maxWidth: 640 }}>
        {/* Header */}
        <div style={{ ...headerStyle, position: 'relative' }}>
          <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 4, marginBottom: 2 }}>MYC</div>
          <div style={{ fontSize: 10, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase' }}>Attendance Review Kiosk</div>
          {kioskAuth && <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>Staff: {kioskAuth.username}</div>}
          {kioskAuth && (
            <button onClick={() => { localStorage.removeItem(KIOSK_AUTH_KEY); setKioskAuth(null); setAuthStep('username'); setAuthUser(''); setAuthOtp(''); }}
              style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 11, color: '#fff', fontWeight: 600 }}>
              Logout
            </button>
          )}
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e8eaf6' }}>
          {[
            { key: 'review', label: 'Review Attendance', icon: '📋' },
            { key: 'leaves', label: 'Leaves Approval', icon: '📝' },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              flex: 1, padding: '12px 8px', border: 'none', cursor: 'pointer',
              background: activeTab === t.key ? '#fff' : '#f5f5f5',
              color: activeTab === t.key ? '#1a237e' : '#64748b',
              fontWeight: activeTab === t.key ? 800 : 600,
              fontSize: 13, borderBottom: activeTab === t.key ? '3px solid #1a237e' : '3px solid transparent',
              transition: 'all 0.2s',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ══ REVIEW ATTENDANCE TAB ══ */}
        {activeTab === 'review' && (
          <div style={{ padding: '16px 16px 24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1a237e' }}>Uniform Review</h3>
              <input type="date" value={reviewDate} onChange={e => setReviewDate(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, color: '#333' }} />
            </div>

            {reviewLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#888' }}>Loading selfies...</div>
            ) : selfies.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#999', fontSize: 14 }}>No selfies found for this date.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {selfies.map(rec => (
                  <div key={rec._id} style={{
                    background: '#f8fafc', borderRadius: 14, overflow: 'hidden',
                    border: '2px solid ' + (rec.uniformStatus === 'approved' ? '#43a047' : rec.uniformStatus === 'issue' ? '#ef5350' : '#e2e8f0'),
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}>
                    <div onClick={() => setZoomImg(API + '/myattendance/selfie-image/' + rec.date + '/' + rec.username)}
                      style={{ cursor: 'pointer', position: 'relative' }}>
                      <SecureImg
                        src={API + '/myattendance/selfie-image/' + rec.date + '/' + rec.username}
                        alt={rec.username}
                        style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                        token={kioskAuth?.token}
                        coords={coords}
                      />
                      <span style={{
                        position: 'absolute', top: 6, right: 6,
                        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                        background: rec.uniformStatus === 'approved' ? '#43a047' : rec.uniformStatus === 'issue' ? '#ef5350' : '#ff9800',
                        color: '#fff',
                      }}>
                        {rec.uniformStatus === 'approved' ? 'Approved' : rec.uniformStatus === 'issue' ? 'Issue' : 'Pending'}
                      </span>
                    </div>

                    <div style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: '#333', marginBottom: 2 }}>{rec.username}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>In: {fmtTime(rec.checkInTime)}</div>
                      {rec.uniformReviewedAt && (
                        <div style={{ fontSize: 10, color: rec.uniformStatus === 'approved' ? '#2e7d32' : '#d97706', marginTop: 2 }}>
                          {rec.uniformStatus === 'approved' ? 'Approved' : 'Reviewed'}: {fmtTime(rec.uniformReviewedAt)}{rec.uniformReviewedBy ? ' by ' + rec.uniformReviewedBy : ''}
                        </div>
                      )}
                      {rec.uniformStatus === 'issue' && rec.uniformIssues?.length > 0 && (
                        <div style={{ fontSize: 10, color: '#ef5350', marginTop: 3 }}>{rec.uniformIssues.join(', ')}</div>
                      )}

                      {rec.uniformStatus === 'pending' && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button onClick={() => handleApprove(rec._id)} style={{
                            flex: 1, padding: '6px', borderRadius: 8, border: 'none',
                            background: '#43a047', color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer',
                          }}>
                            ✓ Approve
                          </button>
                          <button onClick={() => handleFlagIssue(rec._id)} style={{
                            flex: 1, padding: '6px', borderRadius: 8, border: 'none',
                            background: '#ef5350', color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer',
                          }}>
                            ✕ Issue
                          </button>
                        </div>
                      )}
                      {rec.uniformStatus !== 'approved' && (
                        <button onClick={() => handleDeleteSelfie(rec._id, rec.username)} style={{
                          width: '100%', padding: '5px', borderRadius: 8, border: '1px solid #e0e0e0',
                          background: '#fff', color: '#888', fontWeight: 600, fontSize: 10, cursor: 'pointer', marginTop: 6,
                        }}>
                          🔄 Request Re-upload
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Issue picker modal */}
            {reviewingId && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
              }} onClick={() => setReviewingId(null)}>
                <div onClick={e => e.stopPropagation()} style={{
                  background: '#fff', borderRadius: 16, padding: '20px 24px', maxWidth: 360, width: '100%',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}>
                  <h4 style={{ margin: '0 0 14px', fontSize: 15, color: '#1a237e' }}>Flag Issue</h4>
                  {ISSUE_OPTIONS.map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedIssues.includes(opt)}
                        onChange={() => setSelectedIssues(prev => prev.includes(opt) ? prev.filter(x => x !== opt) : [...prev, opt])} />
                      {opt}
                    </label>
                  ))}
                  {selectedIssues.includes('Other') && (
                    <input type="text" placeholder="Describe issue..." value={otherText}
                      onChange={e => setOtherText(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, marginTop: 8, boxSizing: 'border-box' }} />
                  )}
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button onClick={() => setReviewingId(null)} style={{
                      flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #e0e0e0',
                      background: '#fff', color: '#666', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    }}>Cancel</button>
                    <button onClick={submitIssue} style={{
                      flex: 1, padding: '10px', borderRadius: 8, border: 'none',
                      background: '#ef5350', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}>Submit Issue</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ LEAVES APPROVAL TAB ══ */}
        {activeTab === 'leaves' && (
          <div style={{ padding: '16px 16px 24px' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#1a237e' }}>Leaves Approval</h3>

            {leavesLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#888' }}>Loading leaves...</div>
            ) : pendingLeaves.length === 0 && otherLeaves.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#999', fontSize: 14 }}>No leave requests found.</div>
            ) : (
              <>
                {/* Pending leaves */}
                {pendingLeaves.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#d97706', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Pending Approval ({pendingLeaves.length})
                    </div>
                    {pendingLeaves.map(l => (
                      <div key={l._id} style={{
                        background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 12,
                        padding: '12px 14px', marginBottom: 10,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{l.username || l.resourceId}</div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                              {fmtDate(l.start)}{l.end && l.start !== l.end ? ' — ' + fmtDate(l.end) : ''}
                            </div>
                          </div>
                          <span style={{ padding: '2px 10px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8' }}>
                            Applied
                          </span>
                        </div>
                        {l.title && <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>{l.title}</div>}
                        {/* Comment input */}
                        <input type="text" placeholder="Add comment (optional)..."
                          value={leaveComment[l._id] || ''}
                          onChange={e => setLeaveComment(prev => ({ ...prev, [l._id]: e.target.value }))}
                          style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, marginBottom: 8, boxSizing: 'border-box', background: '#fff' }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => handleLeaveAction(l._id, 'approved')} style={{
                            flex: 1, padding: '8px', borderRadius: 8, border: 'none',
                            background: '#22c55e', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                          }}>
                            ✓ Approve
                          </button>
                          <button onClick={() => handleLeaveAction(l._id, 'cancelled')} style={{
                            flex: 1, padding: '8px', borderRadius: 8, border: 'none',
                            background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                          }}>
                            ✕ Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recent leaves (non-pending) */}
                {otherLeaves.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Recent Leaves
                    </div>
                    {otherLeaves.map(l => {
                      var st = l.approved;
                      var stColor = st === 'approved' ? '#16a34a' : st === 'cancelled' ? '#dc2626' : '#6b7280';
                      var stBg = st === 'approved' ? '#dcfce7' : st === 'cancelled' ? '#fee2e2' : '#f3f4f6';
                      var stLabel = st === 'approved' ? 'Approved' : st === 'cancelled' ? 'Rejected' : st === 'auto-attendance' ? 'Auto' : (st || 'Pending');
                      return (
                        <div key={l._id} style={{
                          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                          padding: '10px 12px', marginBottom: 8,
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 12, color: '#334155' }}>{l.username || l.resourceId}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{fmtDate(l.start)}</div>
                            {l.title && <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{l.title}</div>}
                          </div>
                          <span style={{ padding: '2px 10px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: stBg, color: stColor }}>
                            {stLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Zoom image modal */}
      {zoomImg && (
        <div onClick={() => setZoomImg(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}>
          <SecureImg src={zoomImg} alt="Selfie" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 }}
            token={kioskAuth?.token} coords={coords} />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 99999,
          background: toast.type === 'success' ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
          color: '#fff', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 700,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)', maxWidth: '90vw',
          animation: 'slideUp 0.3s ease',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
